import { api_fetch } from "@/api/client.tsx"
import { toast } from "@/components/ui/use-toast"
import { addBigWizInProgress, removeBigWizInProgress } from "@/deduction_table/triage_state"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"

interface RunSelectedResponse {
  ran: number
  message: string
}

/**
 * Run BigWiz on the given deductions. Shared by the table's bulk action and the
 * deduction detail page so both get the same idempotency key, in-progress spinner
 * state, toasts, and refresh behaviour.
 *
 * The request is synchronous server-side (one LLM call per deduction), so it can take
 * a while; callers should disable their control until it resolves.
 */
export async function run_big_wiz(deduction_ids: string[]): Promise<boolean> {
  if (deduction_ids.length === 0) return false

  // One key per gesture so a resubmitted/retried request is a server-side no-op.
  const idempotency_key = crypto.randomUUID()

  addBigWizInProgress(deduction_ids)
  toast({
    title: "BigWiz running",
    description: `Running on ${deduction_ids.length} deduction${deduction_ids.length !== 1 ? "s" : ""}…`,
  })

  try {
    const res = await api_fetch<RunSelectedResponse>("/agent/run-selected", {
      body: { deduction_ids, idempotency_key },
    })
    if (!res.ok) {
      toast({
        title: "BigWiz failed",
        description: res.error.message ?? "Request failed",
        variant: "destructive",
      })
      return false
    }
    toast({ title: "BigWiz finished", description: res.value.data.message })
    triggerRefreshFlag()
    return true
  } finally {
    removeBigWizInProgress(deduction_ids)
  }
}
